import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGame } from "@/context/GameContext";
import { saveGame, loadGame } from "@/lib/payments";
import { Sound } from "@/lib/sound";
import { Copy, Save, Download } from "lucide-react";
import { toast } from "sonner";

const SaveLoadModal = ({ open, onClose }) => {
  const { state, setSaveCode, loadState } = useGame();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const doSave = async () => {
    setBusy(true);
    try {
      const { saveCode, ...rest } = state;
      const res = await saveGame(rest, saveCode);
      setSaveCode(res.code);
      Sound.success();
      toast.success("Game saved!");
    } catch (e) {
      Sound.error();
      toast.error("Couldn't save right now. Try again.");
    }
    setBusy(false);
  };

  const doLoad = async () => {
    if (!code.trim()) return;
    if (!window.confirm("Load this game? It replaces the game on this device.")) return;
    setBusy(true);
    try {
      const res = await loadGame(code);
      loadState(res.state, res.code);
      Sound.fanfare();
      toast.success(`Welcome back, ${res.state.playerName || "boss"}!`);
      onClose();
    } catch (e) {
      Sound.error();
      toast.error(e?.response?.data?.detail || "No game found for that code.");
    }
    setBusy(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.saveCode);
      toast.success("Code copied!");
    } catch (e) {
      toast.error("Couldn't copy — write it down instead.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-3xl border-4 border-sky-300 bg-white p-6" data-testid="save-load-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-slate-900">💾 Save &amp; Load</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 font-medium -mt-1">
          Your game saves on this device automatically. Use a <b>save code</b> to play on another device or keep a backup.
        </p>

        <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-4 space-y-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-sky-700">My save code</div>
          {state.saveCode ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 font-display text-2xl font-bold tracking-widest text-slate-900 bg-white border-2 border-sky-300 rounded-xl px-3 py-2 text-center" data-testid="save-code">
                {state.saveCode}
              </div>
              <Button onClick={copy} data-testid="copy-code-btn" className="bg-white hover:bg-sky-100 text-sky-800 border-2 border-sky-300 rounded-xl btn-3d h-12 w-12 p-0">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <p className="text-sm font-bold text-slate-600">No code yet — save once to get one.</p>
          )}
          <Button onClick={doSave} disabled={busy} data-testid="save-game-btn" className="w-full bg-sky-500 hover:bg-sky-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-sky-600 py-5">
            <Save className="w-4 h-4 mr-1" /> {state.saveCode ? "Save again (same code)" : "Save & get my code"}
          </Button>
          <p className="text-[11px] text-slate-500 font-semibold">Write the code down or ask a parent to keep it. Anyone with the code can load this game.</p>
        </div>

        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 space-y-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Load a game</div>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="KBB-XXXX-XXXX" maxLength={14} className="rounded-xl border-2 border-amber-300 font-bold tracking-widest uppercase" data-testid="load-code-input" onKeyDown={(e) => e.key === "Enter" && doLoad()} />
            <Button onClick={doLoad} disabled={busy || !code.trim()} data-testid="load-game-btn" className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-heading font-black rounded-xl btn-3d border-2 border-amber-500">
              <Download className="w-4 h-4 mr-1" /> Load
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveLoadModal;
